import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import { DateUtils } from 'src/app/shared/utils/date.utils';
import getScoreTypeDetails from 'src/app/shared/utils/score-type-details';
import { ChartDataService } from '../../services/chart-data.service';
import { ChartData } from '../../services/chart-data.models';

@Component({
  selector: 'app-score-trending-chart',
  templateUrl: './score-trending-chart.component.html',
  styleUrls: ['./score-trending-chart.component.scss'],
})
export class ScoreTrendingChartComponent implements OnChanges {
  @Input() timeperiod: string = '';
  @Input() scoreType: string = 'sentiment';
  @Input() companyName: string = '';

  private readonly ONE_DAY_MS = 24 * 3600 * 1000;

  trendingChartData: Highcharts.SeriesOptionsType[] = [];
  yAxisLabelFormat: string = '{value}';
  xAxisTickInterval: number | undefined;
  isLoading = false;
  tooltipOptions: Highcharts.TooltipOptions = {
    backgroundColor: 'rgba(44, 44, 44, 0.90)',
    borderColor: '#434343',
    borderRadius: 3,
    borderWidth: 1,
    style: {
      color: '#fff',
    },
  };

  constructor(private chartDataService: ChartDataService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['timeperiod'] ||
      changes['scoreType'] ||
      changes['companyName']
    ) {
      this.initChart();
    }
  }

  private initChart() {
    this.isLoading = true;
    const timeframeLength = DateUtils.chartFromTo(this.timeperiod).period;

    if (timeframeLength > 60) {
      this.xAxisTickInterval = 30 * this.ONE_DAY_MS;
    } else if (timeframeLength > 20) {
      this.xAxisTickInterval = 7 * this.ONE_DAY_MS;
    } else if (timeframeLength > 10) {
      this.xAxisTickInterval = 3 * this.ONE_DAY_MS;
    } else {
      this.xAxisTickInterval = undefined;
    }

    this.yAxisLabelFormat =
      this.scoreType.toLowerCase() === 'sentiment' ? '{value}%' : '{value}';

    this.chartDataService
      .fetchTrendingChartData(this.timeperiod, this.scoreType, this.companyName)
      .subscribe((data) => {
        this.trendingChartData = this.buildSeries(data, timeframeLength);
        this.isLoading = false;
      });
  }

  private buildSeries(
    data: ChartData,
    timeframeLength: number,
  ): Highcharts.SeriesOptionsType[] {
    if (this.scoreType.toLowerCase() === 'esg') {
      return [
        {
          type: 'line',
          name: getScoreTypeDetails(this.scoreType).label,
          data: data.score,
          color: '#FFD700',
        },
        { type: 'line', name: 'E', data: data.scoreE, color: '#00FF7F' },
        { type: 'line', name: 'S', data: data.scoreS, color: '#9D69FF' },
        { type: 'line', name: 'G', data: data.scoreG, color: '#00FFFF' },
      ];
    }

    if (this.scoreType.toLowerCase() === 'sentiment') {
      const shortPeriod = timeframeLength <= 7 ? 1 : 7;
      const longPeriod = timeframeLength <= 7 ? 7 : 30;
      return [
        {
          type: 'line',
          name:
            shortPeriod === 1
              ? `${shortPeriod} day Sentiment`
              : `${shortPeriod} days Sentiment`,
          data: data.sentimentScoreShort,
          color: '#FFD700',
          tooltip: { valueSuffix: '%', valueDecimals: 0 },
        },
        {
          type: 'line',
          name: `${longPeriod} days Sentiment`,
          data: data.sentimentScoreLong,
          color: '#9D69FF',
          tooltip: { valueSuffix: '%', valueDecimals: 0 },
        },
      ];
    }

    return [
      {
        type: 'line',
        name: getScoreTypeDetails(this.scoreType).label,
        data: data.score,
        color: '#FFD700',
      },
    ];
  }
}
